import { z } from 'zod';
import { blankToUndefined } from '@/helpers/trimData';
import type { IntlFormatters } from 'react-intl';

export const GLN_LENGTH = 13;
export const EINVOICE_EMAIL_MAX_LENGTH = 100;

const GLN_PATTERN = /^\d{13}$/;

type FormatMessage = IntlFormatters['formatMessage'];

export const normalizeGlnInput = (value: string): string =>
  value.replace(/\D/g, '').slice(0, GLN_LENGTH);

export const optionalTrimmedText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  return blankToUndefined(value.trim());
};

export const glnFieldError = (
  value: unknown,
  formatMessage: FormatMessage,
): true | string => {
  const gln = optionalTrimmedText(value);
  if (!gln) return true;
  if (GLN_PATTERN.test(gln)) return true;

  return formatMessage({
    defaultMessage: 'GLN-nummer måste bestå av exakt 13 siffror',
  });
};

export const einvoiceEmailFieldError = (
  value: unknown,
  formatMessage: FormatMessage,
): true | string => {
  const email = optionalTrimmedText(value);
  if (!email) return true;

  if (!z.string().email().safeParse(email).success) {
    return formatMessage({
      defaultMessage: 'Ogiltig e-postadress',
    });
  }

  if (email.length > EINVOICE_EMAIL_MAX_LENGTH) {
    return formatMessage(
      { defaultMessage: 'Maximalt {length} tecken' },
      { length: EINVOICE_EMAIL_MAX_LENGTH },
    );
  }

  return true;
};

export const validateInvoicingIdentifiers = ({
  values,
  ctx,
  formatMessage,
}: {
  values: { gln?: string; einvoice_email?: string };
  ctx: z.RefinementCtx;
  formatMessage: FormatMessage;
}) => {
  const glnError = glnFieldError(values.gln, formatMessage);
  if (glnError !== true) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['gln'],
      message: glnError,
    });
  }

  const email = optionalTrimmedText(values.einvoice_email);
  if (
    email &&
    z.string().email().safeParse(email).success &&
    email.length > EINVOICE_EMAIL_MAX_LENGTH
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['einvoice_email'],
      message: formatMessage(
        { defaultMessage: 'Maximalt {length} tecken' },
        { length: EINVOICE_EMAIL_MAX_LENGTH },
      ),
    });
  }
};
